import {
  AutocompleteMenuSelector,
  ErrorChip,
  Image,
} from '@starsoft/common/components';
import styles from './styles.module.scss';
import { usePlayerStatistics } from '@/api/players';
import { ProfileInfoPicureProps } from './props';
import { useAvatars } from '@starsoft/common/api';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { UpdatePlayerInputDto } from '@/services/dto/players/update-player-input.dto';
import { useCallback, useMemo } from 'react';
import { Avatar } from '@starsoft/common/models';
import { ProfileInfoPicureOption } from './Option';
import { ProfileInfoPicureOptionSkeleton } from './Option/Skeleton';
import { useProfileModalVariant } from '@/hooks/theme/variants/useProfileModalVariant';
import ProfileInfoPicureRenderer from './Renderer';

export function ProfileInfoPicure({ isEditing }: ProfileInfoPicureProps) {
  const { setValue, watch }: UseFormReturn<UpdatePlayerInputDto> =
    useFormContext<UpdatePlayerInputDto>();

  const photo = watch('avatarId');

  const { player, isPending } = usePlayerStatistics();
  const {
    avatars,
    isLoading,
    refetch,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useAvatars({ enabled: isEditing });

  const currentAvatar = useMemo(
    () =>
      avatars?.find(avatar =>
        photo ? avatar?.id == photo : avatar?.url == player?.photo,
      ),
    [avatars, photo, player],
  );

  const onSelectAvatar = useCallback(
    (avatar: Avatar) => {
      setValue('avatarId', avatar?.id);
    },
    [setValue],
  );

  if (error) {
    return <ErrorChip action={refetch} />;
  }

  return (
    <div className={styles.container}>
      <AutocompleteMenuSelector
        getOptionLabel={e => e?.name}
        options={avatars}
        getOptionValue={avatar => avatar?.id}
        value={currentAvatar?.id}
        setValue={onSelectAvatar}
        filterBy={['name']}
        isAsync
        hasNextPage={hasNextPage}
        loadMore={fetchNextPage}
        loadingMore={isFetchingNextPage}
        loading={isPending || isLoading}
        hideSearch
        useGridOptions
        isSmallerDrawer
        customComponents={{
          customInput: ({ handleOpen, open, option, disabled }) => (
            <ProfileInfoPicureRenderer
              open={open}
              disabled={disabled}
              isEditing={isEditing}
              option={option}
              player={player}
              handleOpen={handleOpen}
            />
          ),
          customSkeletonInput: () => (
            <Image
              alt="player-photo"
              className={`${styles.container__photo} ${isEditing ? styles['container__photo--editing'] : ''}`}
              width={100}
              height={100}
            />
          ),
          customOption: option => <ProfileInfoPicureOption {...option} />,
          customSkeletonOption: () => <ProfileInfoPicureOptionSkeleton />,
        }}
      />
    </div>
  );
}
